import { hasNonEmptyValue } from 'Lib/Any';
import {
  IImageResizeOptionsModel,
  IImageResizeProps,
  IImageResizeSettings,
  IImageResizeSummary,
  IImageResolution,
} from './types';

const defaultMaxHeight = 2400;
const defaultMaxWidth = 2400;
const defaultQuality = 0.92;

/**
 * Converts a model representing the QEST Platform image resizing options
 * into settings for resizing an image (by applying preset definitions)
 */
export const GetResizeSettingsFromOptionsModel = (
  options: IImageResizeOptionsModel | undefined
): IImageResizeSettings | undefined => {
  if (!options || !options.Enabled) return undefined;

  switch (options!.Preset) {
    case 'extralarge':
      return { maximumDimensions: { width: 4800, height: 4800 }, quality: 0.95 };
    case 'large':
      return { maximumDimensions: { width: 2400, height: 2400 }, quality: 0.92 };
    case 'medium':
      return { maximumDimensions: { width: 1600, height: 1600 }, quality: 0.9 };
    case 'small':
      return { maximumDimensions: { width: 1200, height: 1200 }, quality: 0.9 };
    case 'custom':
      let maxWidth = options!.TargetWidth;
      let maxHeight = options.TargetHeight;
      let quality = options.Quality;

      if ((!maxWidth || isNaN(maxWidth) || maxWidth <= 0) && (!maxHeight || isNaN(maxHeight) || maxHeight <= 0)) {
        maxWidth = defaultMaxWidth;
        maxHeight = defaultMaxHeight;
      } else if (!maxWidth || isNaN(maxWidth!) || maxWidth! <= 0) {
        maxWidth = 32000; //a large number - we want to resize to the target height only
      } else if (!maxHeight || isNaN(maxHeight!) || maxHeight! <= 0) {
        maxHeight = 32000; //a large number - we want to resize to the target width only
      }

      if (!quality || quality <= 0 || quality > 1) {
        quality = defaultQuality;
      }

      return {
        maximumDimensions: {
          width: maxWidth!,
          height: maxHeight!,
        },
        quality: quality,
      };
  }

  // DEFAULT
  return {
    maximumDimensions: {
      width: defaultMaxWidth!,
      height: defaultMaxHeight!,
    },
    quality: defaultQuality,
  };
};

/**
 * Calculates a target resolution for an image given a set of maximum dimensions.
 */
export const CalculateImageResolution = (
  inputDimensions: IImageResolution,
  maxDimensions: IImageResolution
): IImageResolution => {
  let output: IImageResolution = {
    width: inputDimensions.width,
    height: inputDimensions.height,
  };

  if (output.width > maxDimensions.width) {
    output.width = maxDimensions.width;
    output.height = Math.round((inputDimensions.height / inputDimensions.width) * output.width);
  }

  if (output.height > maxDimensions.height) {
    output.height = maxDimensions.height;
    output.width = Math.round((inputDimensions.width / inputDimensions.height) * output.height);
  }

  return output;
};

/**
 * Resizes an image based on the provided settings (using a canvas element)
 */
export const ImageResize = async (data: IImageResizeProps) => {
  return new Promise<IImageResizeSummary>((resolve, reject) => {
    const imageData = new Image();

    imageData.onload = function () {
      var image = this as any;
      const originalResolution: IImageResolution = { width: image.width, height: image.height };

      const newResolution = hasNonEmptyValue(data.resizeSettings.maximumDimensions)
        ? CalculateImageResolution(originalResolution, data.resizeSettings.maximumDimensions!)
        : originalResolution;

      // Dynamically create a canvas element
      var canvas = document.createElement('canvas');
      canvas.width = newResolution.width;
      canvas.height = newResolution.height;
      var ctx = canvas.getContext('2d');
      // Actual resizing
      ctx!.drawImage(imageData, 0, 0, newResolution.width, newResolution.height);
      // Obtain a data url for the image with the intended image type
      // NOTE: If the browser does not support saving images from canvas to this type,
      // it will fallback to image/png - see https://html.spec.whatwg.org/multipage/canvas.html#dom-canvas-todataurl
      var dataurl = canvas.toDataURL(data.imageType, data.resizeSettings.quality);

      return resolve({
        OriginalWidth: image.width,
        OriginalHeight: image.height,
        ResizedWidth: newResolution.width,
        ResizedHeight: newResolution.height,
        ResizedData: dataurl,
      });
    };

    imageData.onerror = function () {
      reject();
    };

    imageData.src = data.imageDataUrl;
  });
};
